<template>
  <div class="admin-box">
    <h2>İş Listesi ve Teklifler</h2>

    <!-- 🔍 Arama -->
    <input v-model="search" placeholder="İş Kodu, Müşteri, Dil vs. ara..." class="search-box" />

    <!-- 🔎 Filtreler -->
    <div class="filters">
      <button
        v-for="status in statusFilters"
        :key="status.value"
        :class="{ active: filter === status.value }"
        @click="() => { filter = status.value; fetchJobs() }"
      >
        {{ status.label }}
      </button>
    </div>

    <!-- 📄 İşler Listesi -->
    <div class="accordion">
      <div class="job-item" v-for="job in paginatedJobs" :key="job.id">
        <div class="job-summary" @click="toggleJob(job.id)">
          <div><strong>{{ formatJobCode(job.id) }}</strong></div>
          <div>{{ formatStatus(job.status) }}</div>
          <div>{{ job.source_lang }} → {{ job.target_lang }}</div>
          <div>{{ formatDate(job.created_at) }}</div>
        </div>

        <div class="job-details" v-if="expanded === job.id">
          <div class="detail-row" v-if="job.assigned_translator_id">
            <span>Tercüman:</span> 
            {{ job.assigned_translator_name || 'Atanmış tercüman bilgisi yok' }}
          </div>
          <div class="detail-row"><span>Müşteri:</span> {{ job.user_name }} {{ job.user_surname }} <em>({{ job.user_email }})</em></div>
          <div class="detail-row"><span>Açıklama:</span> {{ job.description || '-' }}</div>
          <div class="detail-row" v-if="job.specialization">
          <span>Uzmanlık Alanı:</span> {{ job.specialization }}
          </div>
          <div class="detail-row">
            <span>Dosya:</span>
            <a :href="`${baseURL}/uploads/${encodeURIComponent(job.file_name)}`" target="_blank" class="file-link">
              {{ extractOriginalFileName(job.file_name) }}
            </a>
          </div>

          <div class="detail-row"><span>Diller:</span> {{ job.source_lang }} → {{ job.target_lang }}</div>

          <div class="detail-row" v-if="job.translation_type">
            <span>Tercüme Türü:</span> {{ job.translation_type }}
          </div>

          <div class="detail-row" v-if="job.source_lang === 'Osmanlıca' && job.hat_tur_name">
            <span>Hat Türü:</span> {{ job.hat_tur_name }}
          </div>

          <div class="detail-row"><span>Yüklenme Tarihi:</span> {{ formatDate(job.created_at) }}</div>

          <!-- ✅ Atanmış ve ödeme bekleyen/gönderilen durumlar -->
          <div v-if="['waiting_payment', 'in_progress'].includes(job.status)">
            <div class="offer" v-for="offer in assignedOffers(job)" :key="offer.translator_id">
              <p>
                <strong>{{ offer.translator_name }}</strong><br />
                Fiyat: ₺{{ offer.price }}<br />
                Tahmini Teslim: {{ formatDate(offer.delivery_date) }}
              </p>
              <div class="payment-section">
                <button class="btn green small" v-if="job.status === 'waiting_payment'" @click="markAsPaid(job.id)">
                  ✅ Ödeme Yapıldı
                </button>
                <button class="btn danger small" @click="cancelAssignment(job.id)">
                  ❌ İptal Et
                </button>
              </div>
            </div>
          </div>

          <!-- ✅ Henüz atanmamış teklifler -->
          <div v-else-if="job.status !== 'completed' && job.offers.length">
            <div class="offer" v-for="offer in (Array.isArray(job.offers) ? job.offers : [])" :key="offer.translator_id">
              <p>
                <strong>{{ offer.translator_name }}</strong><br />
                Fiyat: ₺{{ offer.price }}<br />
                Tahmini Teslim: {{ formatDate(offer.delivery_date) }}
              </p>
              <input
                type="number"
                v-model="adminPrices[`${job.id}-${offer.translator_id}`]"
                placeholder="Müşteri Fiyatı"
                class="price-input"
              />
              <button class="btn small" @click="assignWithPrice(job.id, offer.translator_id, offer.delivery_date)">
                Ata ve Fiyatı Gönder
              </button>
            </div>
            <button class="btn danger small mt-2" @click="cancelAssignment(job.id)">
              ❌ İptal Et
            </button>
          </div>

          <div v-else-if="job.status === 'completed'" class="completed-block">
            <em class="completed-label">✅ Tamamlandı</em>

            <!-- Tercüman Bilgisi (Zorunlu) -->
            <div class="detail-row">
              <span>Tercüman:</span> 
              {{ job.assigned_translator_name || 'Bilgi yok' }}
            </div>

            <!-- Yüklenen Dosyalar (Varsa) -->
            <template v-if="Array.isArray(job.output_files) && job.output_files.length">
              <div class="detail-row">
                <span>Teslim Edilen Dosyalar:</span>
              </div>
              <ul class="file-list">
                <li v-for="out in (Array.isArray(job.output_files) ? job.output_files : [])" :key="out.file_name">

                  <a
                    :href="`${baseURL}/uploads/output/${encodeURIComponent(out.file_name)}`"
                    target="_blank"
                    class="file-link"
                  >
                    {{ extractOriginalFileName(out.file_name) }}
                  </a>
                  <small>({{ formatDate(out.uploaded_at) }})</small>
                </li>
              </ul>
            </template>
            <div v-else class="detail-row">
              <span>Teslim Edilen Dosya:</span> Henüz yüklenmedi.
            </div>

            <!-- Revize Butonu -->
            <div class="revise-button mt-2">
              <button class="btn orange small" @click="reviseJob(job.id)">
                🔁 Revize Et
              </button>
            </div>
          </div>

          <!-- ❌ Teklif yok -->
          <div v-else>
            <em>Henüz teklif yok.</em>
          </div>
        </div>
      </div>
    </div>

    <!-- ✅ Sayfalama -->
    <div class="pagination">
      <button @click="prevPage" :disabled="page === 1">‹</button>
      <span>Sayfa {{ page }} / {{ totalPages }}</span>
      <button @click="nextPage" :disabled="page === totalPages">›</button>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { getToken } from '@/utils/auth'
import { useRoute } from 'vue-router'

const baseURL = process.env.VUE_APP_API_URL
const jobs = ref([])
const filter = ref('waiting')
const expanded = ref(null)
const adminPrices = ref({})
const search = ref('')
const page = ref(1)
const perPage = 10
const route = useRoute()
const jobCodeQuery = ref(route.query.job_code || '')

const statusFilters = [
  { value: '', label: 'Tümü' },
  { value: 'waiting', label: 'Bekleyen' },
  { value: 'quoted', label: 'Teklif Verilen' },
  { value: 'waiting_payment', label: 'Ödeme Bekliyor' },
  { value: 'in_progress', label: 'Çeviride' },
  { value: 'completed', label: 'Tamamlanan' }
]

async function fetchJobs() {
  const url = filter.value
    ? `${baseURL}/admin/jobs?status=${filter.value}`
    : `${baseURL}/admin/jobs`	

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` }
  })
  jobs.value = await res.json()
}

// 🔁 Filtrelenmiş işler
const filteredJobs = computed(() => {
  let list = [...jobs.value]

  if (filter.value === 'quoted') {
    list = list.filter(j => j.status === 'waiting' && j.offers?.length > 0)
  } else if (filter.value) {
    list = list.filter(j => j.status === filter.value)
  }

  if (search.value) {
    const keyword = search.value.toLowerCase()
    list = list.filter(j =>
      (j.description || '').toLowerCase().includes(keyword) ||
      (j.user_name || '').toLowerCase().includes(keyword) ||
      (j.user_surname || '').toLowerCase().includes(keyword) ||
      (j.file_name || '').toLowerCase().includes(keyword) ||
      (j.specialization || '').toLowerCase().includes(keyword) ||
      formatJobCode(j.id).toLowerCase().includes(keyword)
    )
  }

  return list
})

const paginatedJobs = computed(() => {
  const start = (page.value - 1) * perPage
  return filteredJobs.value.slice(start, start + perPage)
})

const totalPages = computed(() => Math.ceil(filteredJobs.value.length / perPage))

watch([filter, search], () => {
  page.value = 1
})

function nextPage() {
  if (page.value < totalPages.value) page.value++
}

function prevPage() {
  if (page.value > 1) page.value--
}

function toggleJob(id) {
  expanded.value = expanded.value === id ? null : id
}

function assignedOffers(job) {
  return job.offers.filter(o => o.translator_id === job.assigned_translator_id)
}



async function markAsPaid(jobId) {
  const confirmPay = confirm("Bu işi ödeme yapıldı olarak işaretlemek istiyor musunuz?")
  if (!confirmPay) return

  try {
    const res = await fetch(`${baseURL}/admin/job/${jobId}/mark-paid`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`
      }
    })

    const data = await res.json()
    if (!res.ok) return alert(data.message || 'İşaretleme başarısız.')

    alert(data.message || 'Ödeme bilgisi güncellendi.')
    fetchJobs()
  } catch (error) {
    console.error(error)
    alert('Beklenmeyen bir hata oluştu.')
  }
}

async function cancelAssignment(jobId) {
  const confirmCancel = confirm("Bu işin atamasını iptal etmek istiyor musunuz?")
  if (!confirmCancel) return

  try {
    const res = await fetch(`${baseURL}/admin/job/${jobId}/cancel`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`
      }
    })

    const data = await res.json()
    if (!res.ok) return alert(data.message || 'İptal edilemedi.')

    alert(data.message || 'İş iptal edildi.')
    fetchJobs()
  } catch (error) {
    console.error(error)
    alert('İptal sırasında hata oluştu.')
  }
}

async function assignWithPrice(jobId, translatorId, deliveryDate) {
  const key = `${jobId}-${translatorId}`
  const price = adminPrices.value[key]

  if (!price || !translatorId || !deliveryDate) {
    return alert('Tercüman, teslim tarihi ve fiyat gereklidir.')
  }

  const confirmAssign = confirm(`Bu işi ₺${price} fiyatıyla atamak istiyor musunuz?`)
  if (!confirmAssign) return

  try {
    const res = await fetch(`${baseURL}/admin/job/${jobId}/assign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`
      },
      body: JSON.stringify({
        translator_id: translatorId,
        delivery_date: deliveryDate,
        price
      })
    })

    const data = await res.json()
    if (!res.ok) return alert(data.message || 'İş ataması başarısız.')

    alert(data.message || 'İş başarıyla atandı.')
    fetchJobs()
  } catch (error) {
    console.error(error)
    alert('Beklenmeyen bir hata oluştu.')
  }
}

// ✅ Revize butonu için backend'e istek at
async function reviseJob(jobId) {
  const confirmRevise = confirm('Bu işi tekrar düzenlemeye (revizeye) açmak istiyor musunuz?')
  if (!confirmRevise) return

  try {
    const res = await fetch(`${baseURL}/admin/job/${jobId}/revise`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`
      }
    })

    const data = await res.json()
    if (!res.ok) return alert(data.message || 'Revize başarısız.')

    alert(data.message || 'İş revize edildi.')
    fetchJobs()
  } catch (error) {
    console.error(error)
    alert('Revize işlemi sırasında hata oluştu.')
  }
}

// 🔤 Yardımcı fonksiyonlar
function formatStatus(status) {
  const map = {
    waiting: 'Bekliyor',
    in_progress: 'Çeviride',
    completed: 'Tamamlandı',
    quoted: 'Teklif Bekliyor',
    assigned: 'Atandı',
    waiting_payment: 'Ödeme Bekliyor',
    price_sent: 'Fiyat Gönderildi'
  }
  return map[status] || status
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

function formatJobCode(id) {
  return 'JOB-' + String(id).padStart(6, '0')
}

function extractOriginalFileName(filename) {
  return filename?.replace(/^\d+-\d+-/, '') || filename
}
watch(() => route.query.job_code, (newCode) => {
  if (newCode) {
    search.value = newCode
  }
})
// ⏬ Sayfa yüklendiğinde işler çekilsin
onMounted(() => {
  search.value = jobCodeQuery.value || ''
  fetchJobs()
})
</script>


<style scoped>
    .admin-box {
        max-width: 1100px;
        margin: 60px auto;
        background: #fff;
        padding: 30px;
        border-radius: 12px;
        box-shadow: 0 0 8px rgba(0, 0, 0, 0.05);
        color: #222; /* genel metin rengi siyaha yakın */
    }

        .admin-box h2 {
            text-align: center;
            color: #111; /* başlık siyah */
            margin-bottom: 25px;
        }

    .search-box {
        width: 100%;
        padding: 10px;
        margin-bottom: 15px;
        border: 1px solid #ccc;
        border-radius: 6px;
        font-size: 15px;
        color: #111;
    }

    .filters {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
        flex-wrap: wrap;
    }

    .filters button {
        padding: 8px 14px;
        border: none;
        border-radius: 6px;
        background: #ecf0f1;
        cursor: pointer;
        transition: background 0.2s;
        color: #333;
    }

    .filters button.active {
        background: #2980b9;
        color: #fff;
    }

    .accordion {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .job-item {
        border: 1px solid #ccc;
        border-radius: 8px;
        overflow: hidden;
        color: #222;
    }

    .job-summary {
        padding: 12px;
        background-color: #fdfdfd;
        display: flex;
        justify-content: space-between;
        cursor: pointer;
        font-weight: 500;
        color: #111; /* koyu siyah */
        border-bottom: 1px solid #eee;
        transition: background-color 0.2s ease;
    }

        .job-summary:hover {
            background-color: #f0f4ff;
        }
    .job-details {
        padding: 20px;
        background: #fafafa;
        border-top: 1px solid #ddd;
        display: flex;
        flex-direction: column;
        gap: 12px;
        font-size: 15px;
        color: #333;
    }

    .detail-row {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        line-height: 1.6;
    }

        .detail-row span {
            font-weight: 600;
            color: #222;
            min-width: 140px;
        }

    .file-link {
        background-color: #3498db;
        color: white;
        padding: 6px 10px;
        border-radius: 4px;
        text-decoration: none;
        font-size: 14px;
        display: inline-block;
        transition: background 0.3s;
    }

        .file-link:hover {
            background-color: #2980b9;
        }

    .offer {
        background: #f9f9f9;
        padding: 10px;
        border: 1px solid #ccc;
        border-radius: 6px;
        margin-bottom: 10px;
        color: #111;
    }

    .btn.small {
        font-size: 0.85em;
        padding: 4px 10px;
        margin-top: 6px;
        background: #3498db;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
    }

    .price-input {
        width: 100%;
        margin-top: 5px;
        margin-bottom: 5px;
        padding: 6px;
        border-radius: 4px;
        border: 1px solid #ccc;
        color: #111;
    }

    .payment-section {
        margin-top: 20px;
    }

    .btn.green {
        background-color: #2ecc71;
    }

    .pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 20px;
        gap: 12px;
    }

    .pagination button {
        padding: 6px 14px;
        border-radius: 6px;
        border: none;
        background-color: #3498db;
        color: white;
        cursor: pointer;
        transition: background 0.3s;
    }

    .pagination button:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
    /* Tamamlanan işlerdeki revize butonu */
    .completed-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.completed-label {
  font-style: italic;
  color: #2ecc71;
}

.revise-button {
  display: flex;
  justify-content: flex-start;
}

.file-list {
  list-style-type: none;
  padding-left: 0;
  margin-top: 8px;
}

.file-list li {
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-list small {
  color: #666;
  font-size: 0.85em;
}
@media (max-width: 768px) {
  .admin-box {
    padding: 20px 12px;
    margin: 20px 8px;
  }

  .job-summary {
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
    font-size: 14px;
  }

  .job-details {
    font-size: 14px;
    padding: 14px;
  }

  .detail-row {
    flex-direction: column;
    gap: 2px;
  }

  .detail-row span {
    min-width: unset;
  }

  .filters {
    flex-direction: column;
    gap: 8px;
  }

  .filters button {
    width: 100%;
  }

  .search-box {
    font-size: 14px;
    padding: 8px;
  }

  .price-input {
    font-size: 14px;
  }

  .btn.small {
    font-size: 14px;
    width: 100%;
  }

  .pagination {
    flex-direction: column;
    gap: 6px;
  }

  .payment-section {
    flex-direction: column;
    gap: 6px;
  }

  .revise-button {
    justify-content: center;
  }

}

</style>
